import { DragOverEvent, DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { PurchaseOrderItemStatus, PurchaseOrderItem, POItemGroups } from '@domain/purchasing';
import { KanbanColumn } from '../types/types';

export interface KanbanDragState {
  items: PurchaseOrderItem[];
  activeId: string | null;
}

export interface KanbanDragResult {
  newItems: PurchaseOrderItem[];
  shouldCallStatusChange: boolean;
  statusChangeData?: {
    item: PurchaseOrderItem;
    newStatus: PurchaseOrderItemStatus;
  };
  sortUpdates?: Array<{
    itemId: string;
    newSort: number;
  }>;
}

/**
 * Maps sub-statuses to their main column statuses
 */
export function mapToColumnStatus(status: PurchaseOrderItemStatus): PurchaseOrderItemStatus {
  switch (status) {
    case PurchaseOrderItemStatus.PRODUCTION_READY:
    case PurchaseOrderItemStatus.IN_PRODUCTION:
      return PurchaseOrderItemStatus.IN_PRODUCTION;

    case PurchaseOrderItemStatus.ARRIVED:
    case PurchaseOrderItemStatus.IN_TRANSIT:
      return PurchaseOrderItemStatus.IN_TRANSIT;


    case PurchaseOrderItemStatus.IN_DELIVERY:
      return PurchaseOrderItemStatus.IN_DELIVERY;

    case PurchaseOrderItemStatus.AVAILABLE:
      return PurchaseOrderItemStatus.AVAILABLE;

    default:
      return status;
  }
}

/**
 * Determines the target status when dropping on an item or column
 */
export function determineTargetStatus(
  overId: string,
  columns: KanbanColumn[],
  items: PurchaseOrderItem[]
): PurchaseOrderItemStatus | null {
  // Check if we're dropping on a column header
  const targetColumn = columns.find(col => col.id === overId);
  if (targetColumn) {
    return targetColumn.id;
  }

  // We're dropping on an item, determine the column from the item's status
  const overItem = items.find(item => item.id === overId);
  if (overItem) {
    return mapToColumnStatus(overItem.status);
  }

  return null;
}

/**
 * Handles drag over events for real-time reordering and visual feedback
 */
export function handleDragOver(
  event: DragOverEvent,
  items: PurchaseOrderItem[],
  columns: KanbanColumn[]
): PurchaseOrderItem[] {
  const { active, over } = event;

  if (!over) return items;

  const activeId = active.id as string;
  const overId = over.id as string;

  // Don't do anything if we're over the same item
  if (activeId === overId) return items;

  const activeItem = items.find(item => item.id === activeId);
  if (!activeItem) return items;

  // Check if we're over a column header
  const targetColumn = columns.find(col => col.id === overId);
  if (targetColumn) {
    // Move the item to the target column for smooth visual feedback
    const targetStatus = mapToColumnStatus(targetColumn.id);
    
    // Only move if the status is actually different
    if (activeItem.status !== targetStatus) {
      const updatedItem = {
        ...activeItem,
        status: targetStatus
      };
      
      // Remove the item from its current position and add it to the end of the target column
      const newItems = items.filter(item => item.id !== activeId);
      newItems.push(updatedItem);
      
      return newItems;
    }
  }

  // We're hovering over an item
  const overItem = items.find(item => item.id === overId);
  if (!overItem) return items;

  // If items are in the same status/column, reorder them
  if (activeItem.status === overItem.status) {
    const oldIndex = items.findIndex(item => item.id === activeId);
    const newIndex = items.findIndex(item => item.id === overId);

    if (oldIndex !== newIndex) {
      const reorderedItems = arrayMove(items, oldIndex, newIndex);

      // Immediately update sort values for the reordered column
      updateSortValuesInState(reorderedItems, activeItem.status, activeItem.status);

      return reorderedItems;
    }
  } else {
    // We're dragging over an item in a different column
    // Move the item to the target column for smooth visual feedback
    const targetStatus = mapToColumnStatus(overItem.status);
    
    // Only move if the status is actually different
    if (activeItem.status !== targetStatus) {
      const updatedItem = {
        ...activeItem,
        status: targetStatus
      };
      
      // Remove the item from its current position and add it to the target position
      const newItems = items.filter(item => item.id !== activeId);
      
      // Find the position to insert the item
      const overItemIndex = newItems.findIndex(item => item.id === overId);
      if (overItemIndex !== -1) {
        newItems.splice(overItemIndex, 0, updatedItem);
      } else {
        newItems.push(updatedItem);
      }
      
      return newItems;
    }
  }

  return items;
}

/**
 * Gets placeholder information for where an item will be dropped
 */
export function getPlaceholderInfo(
  event: DragOverEvent,
  items: PurchaseOrderItem[],
  columns: KanbanColumn[]
): { status: PurchaseOrderItemStatus; insertIndex: number } | null {
  const { active, over } = event;

  if (!over) return null;

  const activeId = active.id as string;
  const overId = over.id as string;

  // Don't do anything if we're over the same item
  if (activeId === overId) return null;

  const activeItem = items.find(item => item.id === activeId);
  if (!activeItem) return null;

  let newStatus: PurchaseOrderItemStatus;
  let insertIndex: number;

  // Check if we're over a column header
  const targetColumn = columns.find(col => col.id === overId);
  if (targetColumn) {
    // We're dragging over a column; compute relative to that column's items
    newStatus = mapToColumnStatus(targetColumn.id);
    const targetColumnItems = getUngroupedItemsByStatus(items, newStatus);
    insertIndex = targetColumnItems.length;
  } else {
    // We're hovering over an item
    const overItem = items.find(item => item.id === overId);
    if (!overItem) return null;

    // If items are in the same status/column, no placeholder needed
    if (activeItem.status === overItem.status) {
      return null;
    }

    // Map the over item's status to the appropriate column status
    newStatus = mapToColumnStatus(overItem.status);

    // Calculate insertion position based on sorted order
    const targetColumnItems = getUngroupedItemsByStatus(items, newStatus);
    
    // Find the position where the item would be inserted based on sort order
    const overItemSort = overItem.sort ?? Number.MAX_SAFE_INTEGER;
    
    // Count how many items have a lower sort value than the over item
    let insertPosition = 0;
    for (const item of targetColumnItems) {
      const itemSort = item.sort ?? Number.MAX_SAFE_INTEGER;
      if (itemSort < overItemSort) {
        insertPosition++;
      } else {
        break;
      }
    }
    
    insertIndex = insertPosition;
  }

  // If the item is already in the target column, no need to show placeholder
  if (activeItem.status === newStatus) {
    return null;
  }

  return {
    status: newStatus,
    insertIndex
  };
}

/**
 * Handles drag end events for final status updates and positioning
 */
export function handleDragEnd(
  event: DragEndEvent,
  items: PurchaseOrderItem[],
  columns: KanbanColumn[]
): KanbanDragResult {
  const { active, over } = event;

  if (!over) {
    return { newItems: items, shouldCallStatusChange: false };
  }

  const activeId = active.id as string;
  const overId = over.id as string;

  const activeItem = items.find(item => item.id === activeId);
  if (!activeItem) {
    return { newItems: items, shouldCallStatusChange: false };
  }

  // Determine the target status
  const targetStatus = determineTargetStatus(overId, columns, items);
  if (!targetStatus) {
    return { newItems: items, shouldCallStatusChange: false };
  }

  // Check if we're dropping on a column header
  const targetColumn = columns.find(col => col.id === overId);
  const isDroppingOnColumn = !!targetColumn;

  // Update the status if it's different
  if (targetStatus) {
    const updatedOrderItem = {
      ...activeItem,
      status: targetStatus
    };

    // Update local state
    const newItems = [...items];
    const currentIndex = newItems.findIndex(item => item.id === activeId);

    if (currentIndex !== -1) {
      // Remove the item from its current position
      newItems.splice(currentIndex, 1);

      if (isDroppingOnColumn) {
        // Dropping on column header, add to the end of the column
        newItems.push(updatedOrderItem);
      } else {
        // Dropping on a specific item, position it there
        const overItem = items.find(item => item.id === overId);
        if (overItem) {
          const targetColumnItems = newItems.filter(item => item.status === targetStatus);
          const overItemIndex = targetColumnItems.findIndex(item => item.id === overId);

          if (overItemIndex !== -1) {
            const targetColumnStartIndex = newItems.findIndex(item => item.status === targetStatus);
            const insertIndex = targetColumnStartIndex + overItemIndex;
            newItems.splice(insertIndex, 0, updatedOrderItem);
          } else {
            newItems.push(updatedOrderItem);
          }
        } else {
          newItems.push(updatedOrderItem);
        }
      }

      // Immediately update sort values in the local state for all affected columns
      updateSortValuesInState(newItems, activeItem.status, targetStatus);

      // Calculate sort updates for all items in the affected columns
      const sortUpdates = calculateSortUpdates(newItems, activeItem.status, targetStatus);

      return {
        newItems,
        shouldCallStatusChange: true,
        statusChangeData: {
          item: activeItem,
          newStatus: targetStatus
        },
        sortUpdates
      };
    }
  }

  return { newItems: items, shouldCallStatusChange: false };
}

/**
 * Calculates sort order updates for items in affected columns
 */
function calculateSortUpdates(
  items: PurchaseOrderItem[],
  oldStatus: PurchaseOrderItemStatus,
  newStatus: PurchaseOrderItemStatus
): Array<{ itemId: string; newSort: number }> {
  const sortUpdates: Array<{ itemId: string; newSort: number }> = [];

  // Get all items in the old and new status columns
  const oldStatusItems = items.filter(item => item.status === oldStatus);
  const newStatusItems = items.filter(item => item.status === newStatus);

  // Update sort orders for items in the old status column
  oldStatusItems.forEach((item, index) => {
    if (item.id) {
      sortUpdates.push({
        itemId: item.id,
        newSort: index
      });
    }
  });

  // Update sort orders for items in the new status column
  newStatusItems.forEach((item, index) => {
    if (item.id) {
      sortUpdates.push({
        itemId: item.id,
        newSort: index
      });
    }
  });

  return sortUpdates;
}

/**
 * Updates sort values in the local state for all items in a given status column.
 * This is necessary because the sort values are not directly updated by the DND kit
 * when reordering within the same column.
 */
function updateSortValuesInState(
  items: PurchaseOrderItem[],
  oldStatus: PurchaseOrderItemStatus,
  newStatus: PurchaseOrderItemStatus
) {
  // If reordering within the same column, only update that column
  if (oldStatus === newStatus) {
    const statusItems = items.filter(item => item.status === oldStatus);
    statusItems.forEach((item, index) => {
      if (item.id) {
        item.sort = index;
      }
    });
  } else {
    // If moving between columns, update both columns
    const oldStatusItems = items.filter(item => item.status === oldStatus);
    const newStatusItems = items.filter(item => item.status === newStatus);

    // Update sort values for items in the old status column
    oldStatusItems.forEach((item, index) => {
      if (item.id) {
        item.sort = index;
      }
    });

    // Update sort values for items in the new status column
    newStatusItems.forEach((item, index) => {
      if (item.id) {
        item.sort = index;
      }
    });
  }
}

/**
 * Gets items grouped by their status for display in columns
 */
export function getItemsByStatus(
  items: PurchaseOrderItem[],
  status: PurchaseOrderItemStatus
): Array<{ item: PurchaseOrderItem }> {
  const itemsWithOrders: Array<{ item: PurchaseOrderItem }> = [];

  items.forEach(item => {
    // Combine related statuses into the same columns
    if (status === PurchaseOrderItemStatus.IN_PRODUCTION) {
      if (item.status === PurchaseOrderItemStatus.IN_PRODUCTION ||
        item.status === PurchaseOrderItemStatus.PRODUCTION_READY) {
        itemsWithOrders.push({ item });
      }
    } else if (status === PurchaseOrderItemStatus.IN_TRANSIT) {
      if (item.status === PurchaseOrderItemStatus.IN_TRANSIT ||
        item.status === PurchaseOrderItemStatus.ARRIVED) {
        itemsWithOrders.push({ item });
      }
    } else if (item.status === status) {
      itemsWithOrders.push({ item });
    }
  });

  // Sort items by their sort order
  itemsWithOrders.sort((a, b) => {
    const sortA = a.item.sort ?? Number.MAX_SAFE_INTEGER;
    const sortB = b.item.sort ?? Number.MAX_SAFE_INTEGER;

    // If both items have no sort value, maintain their original order
    if (sortA === Number.MAX_SAFE_INTEGER && sortB === Number.MAX_SAFE_INTEGER) {
      return 0;
    }

    // Items without sort values go to the end
    if (sortA === Number.MAX_SAFE_INTEGER) return 1;
    if (sortB === Number.MAX_SAFE_INTEGER) return -1;

    return sortA - sortB;
  });

  return itemsWithOrders;
}


export function getGroupedItemsByStatus(
  groups: POItemGroups[],
  status: PurchaseOrderItemStatus
): POItemGroups[] {

  const itemsWithOrders: POItemGroups[] = [];

  groups.forEach(group => {
    // Create a filtered copy of the group
    const filteredGroup = {
      ...group,
      items: group.items.filter(item => {
        // Combine related statuses into the same columns
        if (status === PurchaseOrderItemStatus.IN_PRODUCTION) {
          return item.status === PurchaseOrderItemStatus.IN_PRODUCTION ||
            item.status === PurchaseOrderItemStatus.PRODUCTION_READY;
        } else if (status === PurchaseOrderItemStatus.IN_TRANSIT) {
          return item.status === PurchaseOrderItemStatus.IN_TRANSIT ||
            item.status === PurchaseOrderItemStatus.ARRIVED;
        } else {
          return item.status === status;
        }
      })
    };

    // Only include groups that have items after filtering
    if (filteredGroup.items.length > 0) {
      itemsWithOrders.push(filteredGroup);
    }
  });

  return itemsWithOrders;
}

export function getUngroupedItemsByStatus(
  items: PurchaseOrderItem[],
  status: PurchaseOrderItemStatus
): PurchaseOrderItem[] {
  return items.filter(item => {
    // Combine related statuses into the same columns
    if (status === PurchaseOrderItemStatus.IN_PRODUCTION) {
      return item.status === PurchaseOrderItemStatus.IN_PRODUCTION ||
        item.status === PurchaseOrderItemStatus.PRODUCTION_READY;
    } else if (status === PurchaseOrderItemStatus.IN_TRANSIT) {
      return item.status === PurchaseOrderItemStatus.IN_TRANSIT ||
        item.status === PurchaseOrderItemStatus.ARRIVED;
    } else {
      return item.status === status;
    }
  })
}